import React, { useState, useEffect } from 'react';
import {
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Checkbox,
  FormControlLabel,
  Modal,
  Box,
  Typography,
  Container,
  Paper,
  Grid,
  styled,
  CircularProgress,
  SelectChangeEvent,
  ListItemText
} from '@mui/material';
import { httpRequest } from 'src/services/httpRequest';
import { useNavigate, useParams } from 'react-router-dom';

// TypeScript interfaces
interface FormData {
  firstName: string;
  lastName: string;
  business: string;
  federalID: string;
  phone: string;
  email: string;
  fax: string;
  address1: string;
  address2: string;
  city: string;
  state: string;
  zip: string;
  categories: number[]; // Store category IDs
  unsubscribe: boolean;
}

interface ApiResponse {
  success?: boolean;
  message?: string;
  status?: string;
}

interface BusinessResponse {
  status: string;
  message: string;
  data: {
    TownshipID: number;
    FederalID: string;
    Business: string;
    Address1: string;
    Address2: string;
    City: string;
    State: string;
    ZipCode: string;
    ParentID: number;
  };
}

interface CategoryResponse {
  status: string;
  message: string;
  data: {
    totalRecords: number;
    data: Array<{
      CategoryID: number;
      CategoryName: string;
    }>;
  };
}

// For category dropdown display/selection helper
interface CategoryItem {
  id: number;
  name: string;
}

// Styled components
const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  marginTop: theme.spacing(4),
  marginBottom: theme.spacing(4)
}));

const FormSection = styled('div')(({ theme }) => ({
  marginBottom: theme.spacing(3)
}));

const ButtonContainer = styled('div')(({ theme }) => ({
  display: 'flex',
  justifyContent: 'flex-end',
  gap: theme.spacing(2),
  marginTop: theme.spacing(3)
}));

const LoadingContainer = styled('div')({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  marginTop: '20px'
});

const states = [
  'AL',
  'AK',
  'AZ',
  'AR',
  'CA',
  'CO',
  'CT',
  'DE',
  'FL',
  'GA',
  'HI',
  'ID',
  'IL',
  'IN',
  'IA',
  'KS',
  'KY',
  'LA',
  'ME',
  'MD',
  'MA',
  'MI',
  'MN',
  'MS',
  'MO',
  'MT',
  'NE',
  'NV',
  'NH',
  'NJ',
  'NM',
  'NY',
  'NC',
  'ND',
  'OH',
  'OK',
  'OR',
  'PA',
  'RI',
  'SC',
  'SD',
  'TN',
  'TX',
  'UT',
  'VT',
  'VA',
  'WA',
  'WV',
  'WI',
  'WY'
];

const VendorRegistration: React.FC = () => {
  const [formData, setFormData] = useState<FormData>({
    firstName: '',
    lastName: '',
    business: '',
    federalID: '',
    phone: '',
    email: '',
    fax: '',
    address1: '',
    address2: '',
    city: '',
    state: '',
    zip: '',
    categories: [], // Store category IDs
    unsubscribe: false
  });

  const navigate = useNavigate();
  const { state: locationState, township, uuid } = useParams();

  const [openModal, setOpenModal] = useState<boolean>(false);
  const [modalContent, setModalContent] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [, setApiError] = useState<string | null>(null);
  const [federalIdError, setFederalIdError] = useState<string | null>(null);
  const [federalIdEntered, setFederalIdEntered] = useState<boolean>(false);
  const [fetchingBusinessData, setFetchingBusinessData] =
    useState<boolean>(false);
  const [categories, setCategories] = useState<CategoryItem[]>([]);
  const [loadingCategories, setLoadingCategories] = useState<boolean>(false);
  const [isManualInputAllowed, setIsManualInputAllowed] =
    useState<boolean>(false);
  const [zipError, setZipError] = useState<string | null>(null);
  const [openCategorySelect, setOpenCategorySelect] = useState(false);

  // Township ID would normally come from your app state/context or route params
  // const townshipId = 1; // Replace with actual Township ID or get it dynamically

  // Phone number formatting function
  const formatPhoneNumber = (value: string) => {
    const input = value.replace(/\D/g, '').substring(0, 10);
    const first = input.substring(0, 3);
    const middle = input.substring(3, 6);
    const last = input.substring(6, 10);
    let finalValue = '';
    if (input?.length > 6) {
      finalValue = `${first}-${middle}-${last}`;
    } else if (input?.length > 3) {
      finalValue = `${first}-${middle}`;
    } else if (input?.length >= 0) {
      finalValue = input;
    }
    return finalValue;
  };

  const formatFaxNumber = (value: string) => {
    const input = value.replace(/\D/g, '').substring(0, 10); // Keep only the first 10 digits
    const first = input.substring(0, 3);
    const middle = input.substring(3, 6);
    const last = input.substring(6, 10);
    let finalValue = '';
    if (input?.length > 6) {
      finalValue = `${first}-${middle}-${last}`;
    } else if (input?.length > 3) {
      finalValue = `${first}-${middle}`;
    } else if (input?.length >= 0) {
      finalValue = input;
    }
    return finalValue;
  };

  // Fetch categories from API
  const fetchCategories = async () => {
    setLoadingCategories(true);
    try {
      const response = (await httpRequest(
        'GET',
        `/bid_portal/category_get_all/${locationState}/${township}/${uuid}`
      )) as CategoryResponse;

      if (
        response.status === 'success' &&
        response.data &&
        response.data.data
      ) {
        // Filter out inactive categories
        const activeCategories = response.data.data.map((cat) => ({
          id: cat.CategoryID,
          name: cat.CategoryName
        }));

        setCategories(activeCategories);
      } else {
        throw new Error(response.message || 'Failed to retrieve categories');
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
      if (error instanceof Error) {
        setApiError(`Error fetching categories: ${error.message}`);
      } else {
        setApiError('An unknown error occurred while fetching categories');
      }
    } finally {
      setLoadingCategories(false);
    }
  };

  useEffect(() => {
    // Fetch categories from API on component mount
    fetchCategories();
  }, []);

  // Format Federal ID to automatically include hyphen
  const formatFederalId = (value: string): string => {
    // If the value already has a properly positioned hyphen, keep it
    if (value.length >= 3 && value[2] === '-') {
      // Keep the hyphen but ensure other characters are numeric
      const part1 = value.substring(0, 2).replace(/\D/g, '');
      const part2 = value.substring(3).replace(/\D/g, '');
      return `${part1}-${part2}`;
    }

    // Remove all non-numeric characters
    const numbersOnly = value.replace(/\D/g, '');

    // Format with hyphen
    if (numbersOnly.length <= 2) {
      return numbersOnly;
    } else {
      return `${numbersOnly.substring(0, 2)}-${numbersOnly.substring(
        2,
        Math.min(9, numbersOnly.length)
      )}`;
    }
  };

  // Validate Federal ID format (00-0000000)
  const validateFederalId = (id: string): boolean => {
    const regex = /^\d{2}-\d{7}$/;
    return regex.test(id);
  };

  // Fetch business data based on Federal ID
  const fetchBusinessData = async (federalId: string) => {
    setFetchingBusinessData(true);
    setApiError(null);

    try {
      const response = (await httpRequest(
        'GET',
        `/auth/businessDetails/${federalId}/${locationState}/${township}/${uuid}`
      )) as BusinessResponse;

      if (response.status === 'success' && response.data) {
        // Populate form with the data from the API response
        setFormData((prev) => ({
          ...prev,
          // Keep original form fields that are not in the API response
          firstName: prev.firstName,
          lastName: prev.lastName,
          phone: prev.phone,
          email: prev.email,
          fax: prev.fax,
          categories: prev.categories,

          // Update fields that are in the API response
          business: response.data.Business || '',
          federalID: response.data.FederalID || federalId,
          address1: response.data.Address1 || '',
          address2: response.data.Address2 || '',
          city: response.data.City || '',
          state: response.data.State || '',
          zip: response.data.ZipCode || ''
        }));
      } else {
        // Still enable fields even if business data is not found
        setApiError(
          'Business information not found. You may enter the details manually.'
        );
        setIsManualInputAllowed(true);
      }
    } catch (error) {
      console.error('Error fetching business data:', error);
      setIsManualInputAllowed(true);
      if (error instanceof Error) {
        setApiError(
          `Error fetching business data: ${error.message}. You may enter the details manually.`
        );
      } else {
        setApiError(
          'An unknown error occurred while fetching business data. You may enter the details manually.'
        );
      }
    } finally {
      setFetchingBusinessData(false);
      // Enable fields even if fetch fails
      setFederalIdEntered(true);
    }
  };

  const formatZipCode = (value: string): string => {
    // Remove all non-numeric characters
    const numbersOnly = value.replace(/\D/g, '').substring(0, 9); // Limit to 9 digits

    // Format with hyphen for zip codes longer than 5 digits
    if (numbersOnly.length <= 5) {
      return numbersOnly;
    } else {
      return `${numbersOnly.substring(0, 5)}-${numbersOnly.substring(5, 9)}`;
    }
  };

  // Add this validation function
  const validateZipCode = (zip: string): boolean => {
    // Match either 5 digits or 5 digits-4 digits format
    const regex = /^\d{5}(-\d{4})?$/;
    return regex.test(zip);
  };

  // Handle text and checkbox input changes
  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = event.target;

    if (!name) return;

    if (type === 'checkbox') {
      setFormData((prev) => ({
        ...prev,
        [name]: checked
      }));
    } else if (name === 'federalID') {
      // Allow for manual hyphen entry or add automatically
      const formattedValue = formatFederalId(value);

      // Only update if the formatted value is different to avoid cursor jumping
      if (formattedValue !== value) {
        setFormData((prev) => ({
          ...prev,
          [name]: formattedValue
        }));
      } else {
        setFormData((prev) => ({
          ...prev,
          [name]: value
        }));
      }

      // Handle Federal ID validation and fetch business data
      if (formattedValue && validateFederalId(formattedValue)) {
        setFederalIdError(null);
        fetchBusinessData(formattedValue);
        setFederalIdEntered(true);
      } else if (formattedValue) {
        setFederalIdError('Federal Tax ID must be in format XX-XXXXXXX');
        setFederalIdEntered(false);
        // Reset business fields when Federal ID format is invalid
        setFormData((prev) => ({
          ...prev,
          business: '',
          address1: '',
          address2: '',
          city: '',
          state: '',
          zip: '',
          categories: [] // Clear the categories
        }));
        setZipError(null);
      } else {
        setFederalIdError(null);
        setFederalIdEntered(false);
        // Reset all business fields if Federal ID is removed
        setFormData((prev) => ({
          ...prev,
          business: '',
          address1: '',
          address2: '',
          city: '',
          state: '',
          zip: '',
          categories: [] // Clear the categories
        }));
        setZipError(null);
      }
    } else if (name === 'phone' || name === 'fax') {
      // Format phone number automatically
      const formattedValue =
        name === 'phone' ? formatPhoneNumber(value) : formatFaxNumber(value);
      setFormData((prev) => ({
        ...prev,
        [name]: formattedValue
      }));
    } else if (name === 'zip') {
      const formattedValue = formatZipCode(value);
      setFormData((prev) => ({
        ...prev,
        [name]: formattedValue
      }));

      // Validate zip code format
      if (formattedValue && !validateZipCode(formattedValue)) {
        setZipError(
          'ZIP code must be 5 digits (e.g., 18235) or 9 digits (e.g., 18235-2239)'
        );
      } else {
        setZipError(null);
      }
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value
      }));
    }
  };

  // Handle select (dropdown) changes
  const handleSelectChange = (event: SelectChangeEvent<unknown>) => {
    const { name, value } = event.target;

    if (!name) return;

    if (name === 'categories') {
      setFormData((prev) => ({
        ...prev,
        categories: value as number[]
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleClear = () => {
    setFormData({
      firstName: '',
      lastName: '',
      business: '',
      federalID: '',
      phone: '',
      email: '',
      fax: '',
      address1: '',
      address2: '',
      city: '',
      state: '',
      zip: '',
      categories: [],
      unsubscribe: false
    });

    setZipError(null);
    setApiError(null);
    setFederalIdError(null);
    setFederalIdEntered(false);
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    // Validate Federal ID format before submitting
    if (!validateFederalId(formData.federalID)) {
      setFederalIdError('Federal Tax ID must be in format XX-XXXXXXX');
      return;
    }

    setIsLoading(true);
    setApiError(null);

    try {
      const payload = {
        FirstName: formData.firstName,
        LastName: formData.lastName,
        EmailID: formData.email,
        PhoneNumber: formData.phone, // This will be in XXX-XXX-XXXX format
        Address1: formData.address1,
        Address2: formData.address2,
        City: formData.city,
        State: formData.state,
        ZipCode: formData.zip,
        Business: formData.business,
        BidCategoryIDs: formData.categories, // Submit category IDs directly
        FederalID: formData.federalID,
        FaxNumber: formData.fax || '',
        IsAdminRegisterVendor: false,
        UnSubscribeNotify: formData.unsubscribe
      };

      const response = (await httpRequest(
        'POST',
        `/auth/vendor_register/${locationState}/${township}/${uuid}`,
        payload
      )) as ApiResponse;

      if (response.status === 'error') {
        throw new Error(response.message || 'Registration failed');
      }

      setModalContent(
        'Vendor registration has been successfully submitted to the system.'
      );
      setOpenModal(true);
      handleClear();
    } catch (error) {
      console.error('Registration error:', error);
      if (error instanceof Error) {
        setApiError(error.message);
      } else {
        setApiError('An unknown error occurred');
      }
      setModalContent(error.message);
      setOpenModal(true);
    } finally {
      setIsLoading(false);
    }
  };

  const handleModalClose = () => {
    setOpenModal(false);
    if (modalContent.includes('success')) {
      navigate(`/${locationState}/${township}/${uuid}/login`);
    }
  };

  // Determine if a field should be disabled
  const isFieldDisabled = (fieldName: string): boolean => {
    // If manual input is allowed, all fields should be enabled
    if (isManualInputAllowed) {
      return false;
    }

    const disabledFieldsInEditMode = [
      'business',
      'federalID',
      'address1',
      'address2',
      'city',
      'state',
      'zip',
      'email'
    ];

    const disabledFieldsInAddMode = [
      'business',
      'address1',
      'address2',
      'city',
      'state',
      'zip'
    ];

    if (disabledFieldsInEditMode.includes(fieldName)) {
      return true;
    }

    if (disabledFieldsInAddMode.includes(fieldName)) {
      return true;
    }

    // For regular mode, only disable fields until Federal ID is entered and validated
    if (!federalIdEntered) {
      return fieldName !== 'federalID';
    }

    return false;
  };

  // Helper function to get category names for the selected IDs
  const getCategoryNamesByIds = (categoryIds: number[]): string => {
    if (!categories.length || !categoryIds.length) return '';

    const selectedCategories = categories.filter((cat) =>
      categoryIds.includes(cat.id)
    );
    return selectedCategories.map((cat) => cat.name).join(', ');
  };

  return (
    <Container maxWidth="md">
      <StyledPaper elevation={3}>
        <Typography variant="h4" component="h1" gutterBottom align="center">
          Vendor Registration
        </Typography>

        {(fetchingBusinessData || loadingCategories) && (
          <LoadingContainer>
            <CircularProgress size={24} />
            <Typography variant="body2" sx={{ ml: 2 }}>
              {loadingCategories
                ? 'Loading categories...'
                : 'Fetching business information...'}
            </Typography>
          </LoadingContainer>
        )}

        <form onSubmit={handleSubmit}>
          {/* Federal Tax ID Section */}
          <FormSection>
            <Typography variant="h6" gutterBottom>
              Business Identification
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Federal Tax ID"
                  name="federalID"
                  value={formData.federalID}
                  onChange={handleInputChange}
                  required
                  variant="outlined"
                  placeholder="XX-XXXXXXX"
                  helperText={
                    federalIdError ||
                    'Enter Federal Tax ID in format: XX-XXXXXXX'
                  }
                  error={!!federalIdError}
                  disabled={fetchingBusinessData}
                  InputProps={{
                    inputProps: {
                      maxLength: 10 // 2 digits + hyphen + 7 digits
                    }
                  }}
                />
              </Grid>
            </Grid>
          </FormSection>

          {/* Business & Contact Information */}
          <FormSection>
            <Typography variant="h6" gutterBottom>
              Business & Contact Information
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Business Name"
                  name="business"
                  value={formData.business}
                  onChange={handleInputChange}
                  required
                  variant="outlined"
                  disabled={isFieldDisabled('business')}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="First Name"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleInputChange}
                  required
                  variant="outlined"
                  disabled={isFieldDisabled('firstName')}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Last Name"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleInputChange}
                  required
                  variant="outlined"
                  disabled={isFieldDisabled('lastName')}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  required
                  fullWidth
                  label="Phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  variant="outlined"
                  disabled={isFieldDisabled('phone')}
                  placeholder="XXX-XXX-XXXX"
                  inputProps={{ maxLength: 12 }} // Updated to allow for formatted phone
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  variant="outlined"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Fax"
                  name="fax"
                  placeholder="XXX-XXX-XXXX"
                  value={formData.fax}
                  onChange={handleInputChange}
                  variant="outlined"
                  disabled={isFieldDisabled('fax')}
                />
              </Grid>
            </Grid>
          </FormSection>

          {/* Address Information */}
          <FormSection>
            <Typography variant="h6" gutterBottom>
              Address Information
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Address Line 1"
                  name="address1"
                  value={formData.address1}
                  onChange={handleInputChange}
                  required
                  variant="outlined"
                  disabled={isFieldDisabled('address1')}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Address Line 2"
                  name="address2"
                  value={formData.address2}
                  onChange={handleInputChange}
                  variant="outlined"
                  disabled={isFieldDisabled('address2')}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="City"
                  name="city"
                  value={formData.city}
                  onChange={handleInputChange}
                  required
                  variant="outlined"
                  disabled={isFieldDisabled('city')}
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <FormControl fullWidth variant="outlined">
                  <InputLabel>State</InputLabel>
                  <Select
                    name="state"
                    value={formData.state}
                    onChange={handleSelectChange}
                    label="State"
                    required
                    disabled={isFieldDisabled('state')}
                  >
                    <MenuItem value="">
                      <em>Select State</em>
                    </MenuItem>
                    {states.map((state) => (
                      <MenuItem key={state} value={state}>
                        {state}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField
                  fullWidth
                  label="ZIP Code"
                  name="zip"
                  value={formData.zip}
                  onChange={handleInputChange}
                  required
                  variant="outlined"
                  disabled={isFieldDisabled('zip')}
                  placeholder="12345 or 12345-6789"
                  helperText={zipError}
                  error={!!zipError}
                  inputProps={{ maxLength: 10 }} // 5 digits + hyphen + 4 digits
                />
              </Grid>
            </Grid>
          </FormSection>

          {/* Business Categories */}
          <FormSection>
            {/* Add bottom padding */}
            <Typography variant="h6" gutterBottom>
              Business Categories *
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <FormControl fullWidth variant="outlined">
                  <InputLabel id="categories-label">Categories</InputLabel>
                  <Select
                    open={openCategorySelect}
                    onOpen={() => setOpenCategorySelect(true)}
                    onClose={() => setOpenCategorySelect(false)}
                    required
                    labelId="categories-label"
                    multiple
                    name="categories"
                    value={formData.categories}
                    onChange={(e) => {
                      const selectedValues = e.target.value as any[];

                      if (selectedValues.includes('SELECT_ALL')) {
                        const allCategoryIDs = categories.map((cat) => cat.id);
                        const isAllSelected =
                          formData.categories.length === categories.length;

                        const finalCategories = isAllSelected
                          ? []
                          : allCategoryIDs;

                        setFormData((prev) => ({
                          ...prev,
                          categories: finalCategories
                        }));
                      } else {
                        setFormData((prev) => ({
                          ...prev,
                          categories: selectedValues
                        }));
                      }
                    }}
                    label="Categories"
                    renderValue={(selected) =>
                      getCategoryNamesByIds(selected as number[])
                    }
                    disabled={
                      isFieldDisabled('categories') || loadingCategories
                    }
                    MenuProps={{
                      PaperProps: {
                        sx: {
                          maxHeight: 350,
                          borderRadius: 1.5,
                          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
                          marginTop: 1
                        }
                      },
                      anchorOrigin: {
                        vertical: 'bottom',
                        horizontal: 'left'
                      },
                      transformOrigin: {
                        vertical: 'top',
                        horizontal: 'left'
                      }
                    }}
                  >
                    <MenuItem value="SELECT_ALL">
                      <Checkbox
                        checked={
                          formData.categories.length === categories.length
                        }
                        indeterminate={
                          formData.categories.length > 0 &&
                          formData.categories.length < categories.length
                        }
                      />
                      <ListItemText
                        primary="Select All"
                        sx={{ fontWeight: 'bold', color: 'primary.main' }}
                      />
                    </MenuItem>

                    {categories.map((category) => (
                      <MenuItem key={category.id} value={category.id}>
                        <Checkbox
                          checked={formData.categories.includes(category.id)}
                        />
                        <Typography>{category.name}</Typography>
                      </MenuItem>
                    ))}
                    <MenuItem
                      disableRipple
                      sx={{
                        display: 'flex',
                        justifyContent: 'flex-end',
                        borderTop: '1px solid #ddd',
                        mt: 1,
                        pt: 1
                      }}
                    >
                      <button
                        style={{
                          backgroundColor: '#1976d2',
                          color: 'white',
                          padding: '5px 12px',
                          borderRadius: '6px',
                          border: 'none',
                          cursor: 'pointer',
                          fontWeight: '600'
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          setOpenCategorySelect(false);
                        }}
                      >
                        Apply
                      </button>
                    </MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </FormSection>

          {/* Preferences */}
          <FormSection>
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.unsubscribe}
                  onChange={handleInputChange}
                  name="unsubscribe"
                  disabled={isFieldDisabled('unsubscribe')}
                />
              }
              label="Unsubscribe from notifications"
            />
          </FormSection>

          {/* Buttons */}
          <ButtonContainer>
            <Button
              variant="outlined"
              color="secondary"
              onClick={handleClear}
              disabled={isLoading}
            >
              Clear
            </Button>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={isLoading || !validateFederalId(formData.federalID)}
            >
              {isLoading ? 'Submitting...' : 'Submit Registration'}
            </Button>
          </ButtonContainer>

          {isLoading && (
            <LoadingContainer>
              <CircularProgress size={24} />
            </LoadingContainer>
          )}
        </form>
      </StyledPaper>

      {/* Result Modal */}
      <Modal
        open={openModal}
        onClose={handleModalClose}
        aria-labelledby="modal-title"
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 400,
            bgcolor: 'background.paper',
            boxShadow: 24,
            p: 4,
            borderRadius: 2,
            textAlign: 'center'
          }}
        >
          {modalContent.includes('success') ? (
            <>
              <Box sx={{ color: 'success.main', mb: 2, fontSize: '3rem' }}>
                ✓
              </Box>
              <Typography
                id="modal-title"
                variant="h5"
                component="h2"
                gutterBottom
                color="primary"
              >
                Registration Successful!
              </Typography>
              <Typography variant="body1" sx={{ mb: 3 }}>
                {modalContent}
              </Typography>
            </>
          ) : (
            <>
              <Box sx={{ color: 'error.main', mb: 2, fontSize: '3rem' }}>✗</Box>
              <Typography
                id="modal-title"
                variant="h5"
                component="h2"
                gutterBottom
                color="error"
              >
                Registration Failed
              </Typography>
              <Typography variant="body1" sx={{ mb: 3 }}>
                {modalContent}
              </Typography>
            </>
          )}
          <Button
            onClick={handleModalClose}
            variant="contained"
            color={modalContent.includes('success') ? 'primary' : 'error'}
            sx={{ minWidth: 120 }}
          >
            Close
          </Button>
        </Box>
      </Modal>
    </Container>
  );
};

export default VendorRegistration;
